import CommonDelete from "@/components/common/Delete";
import CommonList from "@/components/common/List";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ColumnDef } from "@tanstack/react-table";
import { MoreVerticalIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import CommonTableList from "@/components/common/TableList";
import { useCallback, useState } from "react";
import ClientsDetails from "./ClientDetails";
import { ResourceContextProvider } from "react-admin";
import { TaxiClient } from "@/interface/entityInterface";
import ClientCreate from "./ClientCreate";
import ClientEdit from "./ClientEdit";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSipContext } from "@/context/SipContext";
import { Badge } from "@/components/ui/badge";
import { makeCall } from "@/libraries/softphone/call-helpers";
import { Input } from "@/components/ui/input";
import { DataTableFacetedFilterCustom } from "@/components/data-table/data-table-faceted-filter-custom";
import { getFormattedDate } from "@/utils/generateTimeSlots";

const ClientsList = () => {
  const globalSipContext = useSipContext();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [client, setClient] = useState<TaxiClient | null>(null);
  const [callDialog, setDialog] = useState(false);
  const [callingNumber, setCallingNumber] = useState("");
  const [clientName, setName] = useState("")
  const [sources, setSources] = useState<string[]>([])

  const handleRowClick = (row: TaxiClient) => {
    setDetailsOpen(true);
    setClient(row);
  };

  const handleCall = useCallback(() => {
    makeCall(callingNumber, globalSipContext);
  }, [callingNumber, globalSipContext]);

  const columns: ColumnDef<TaxiClient>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "id",
      header: "ID",
      cell: ({ row }) => {
        return (
          <div className="capitalize">
            {row.getValue("id") || row.original.user_client_id}
          </div>
        );
      },
    },
    {
      accessorKey: "source",
      header: "Client from",
      cell: ({ row }) => (
        <Badge variant="outline" className="text-gray-500">
          {row.getValue("source")}
        </Badge>
      ),
    },
    {
      accessorKey: "phone_number",
      header: "Phone number",
      cell: ({ row }) => {
        const { phone_number } = row.original;
        return globalSipContext.hasFreePBX ? (
          <Button
            variant="outline"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              setDialog(true);
              setCallingNumber(phone_number);
            }}
          >
            {phone_number}
          </Button>
        ) : (
          <div>{row.getValue("phone_number")}</div>
        );
      },
    },
    {
      accessorKey: "name",
      header: "Full Name",
      cell: ({ row }: any) => (
        <div className="capitalize">{row.getValue("name")}</div>
      ),
    },
    {
      accessorKey: "createdAt",
      header: "Joined at",
      cell: ({ row }: any) => <div>{getFormattedDate(row.getValue("createdAt"))}</div>,
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <Button
              variant="ghost"
              className="flex size-8 text-muted-foreground data-[state=open]:bg-muted"
              size="icon"
            >
              <MoreVerticalIcon />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-32">
            <ClientEdit prop={row.original.id} />
            <DropdownMenuSeparator />
            <CommonDelete id={row.original.id} resource="taxi-clients" />
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const renderToolbarFilters = (table: any) => {
    return (
      <>
        <Input
          placeholder={`Filter by name`}
          onChange={(e: React.FormEvent<HTMLInputElement>) =>
            setName(e.currentTarget.value)
          }
          value={clientName}
          className="max-w-md"
        />

        <DataTableFacetedFilterCustom
          column={table.getColumn("source")}
          title="Source"
          selectedValues={
            sources.length > 0
              ? new Set(sources)
              : new Set<string>()
          }
          onChange={(e: string[]) => setSources(e)}
          options={[{label: "Mobile app", value: "Mobile app"}, {label: "Web app", value: "Web app"}]}
        />
      </>
    );
  };

  return (
    <>
      <ResourceContextProvider value="taxi-clients">
        <CommonList filters={{
          sources, 
          clientName
        }}>
          <CommonTableList
            columns={columns}
            filterSearch="name"
            create={<ClientCreate />}
            rowClick={handleRowClick}
            renderToolbarFilters={renderToolbarFilters}
          />
        </CommonList>
      </ResourceContextProvider>
      {detailsOpen && (
        <ClientsDetails
          open={detailsOpen}
          setOpen={setDetailsOpen}
          client={client}
        />
      )}
      <Dialog open={callDialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Call</DialogTitle>
            <DialogDescription>
              Are you sure you want to call{" "}
              <span className="font-bold">{callingNumber}</span>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button
              variant="link"
              onClick={() => {
                setDialog(false);
                setCallingNumber("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={() => {
                setDialog(false);
                handleCall();
              }}
            >
              Call
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClientsList;
